import { useMemo } from 'react'
import { effectiveAimStatus, IAimStatus, IUser, liveAimStatus } from '@common/interfaces/user'
import { useAimStatusTick } from '../../../hooks/useAimStatusTick'
import { useSelector } from '../../../redux/hooks'
import { aimProfileByUserId } from '../../../redux/presence/selectors'
import { list as userRoster } from '../../../redux/user/selectors'

/** The status columns, as they arrive on any user-shaped payload. */
export type IAimStatusSource = Pick<
  IUser,
  | 'aimStatus'
  | 'aimStatusEmoji'
  | 'aimStatusExpiresAt'
  | 'aimCalendarStatus'
  | 'aimCalendarStatusEmoji'
  | 'aimCalendarStatusExpiresAt'
>

export interface IAimStatusOptions {
  /**
   * An already-resolved status, for callers whose payload carries one — channel
   * and conversation-group member summaries resolve it server-side. Lets the row
   * be right on first paint rather than only once a push arrives.
   */
  status?: IAimStatus | null
  /** The raw user row, for callers that hold one instead. */
  statusSource?: Partial<IAimStatusSource>
}

/**
 * One person's current status, from whichever source is freshest.
 *
 * A hook rather than a second connected component, because the status now
 * renders beside a name rather than on an avatar, and the surfaces that show it
 * differ too much to share a wrapper: an option list builds a plain label
 * string, a sidebar row is a flex child, a mention entry holds only an id.
 *
 * Order is most-current first: a live push, then whatever the caller was handed,
 * then the roster. The roster is what makes a status show on a surface holding
 * nothing but an id; when it has not been loaded the status is simply absent,
 * which costs nothing else on the row.
 *
 * Expiry is applied last, against a ticking instant — nothing pushes the fact
 * that a meeting ended.
 */
export const useAimStatus = (
  userId: string,
  { status, statusSource }: IAimStatusOptions = {},
): IAimStatus | null => {
  const now = useAimStatusTick()
  const aimProfiles = useSelector(aimProfileByUserId)
  const roster = useSelector(userRoster)

  return useMemo(() => {
    const pushed = aimProfiles[userId]
    if (pushed) return liveAimStatus(pushed.aimStatus, now)
    // Already resolved by the server, so only expiry is left to apply.
    if (status !== undefined) return liveAimStatus(status, now)

    const source = statusSource ?? roster?.data?.data?.find((row) => row.id === userId)
    // `effectiveAimStatus`, not `toAimStatus`: the latter reads only the three
    // manual columns, so a calendar-derived status was silently dropped on a
    // freshly loaded page and appeared only if a push happened to arrive. This is
    // the same precedence the server applies in `aimStatusOf`, which is the point
    // of having it in `common/`. It applies expiry itself.
    return source ? effectiveAimStatus(source, now) : null
  }, [aimProfiles, userId, status, statusSource, roster, now])
}

export default useAimStatus
